import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useParams } from '@tanstack/react-router'
import {
  addDependency,
  detachRelation,
  serviceCatalogDependableQuery,
  serviceCatalogDependenciesQuery,
  type DependenciesPage,
} from '../api/serviceCatalog'
import { moduleOf, shellQuery } from '../api/shell'
import { Forbidden, isForbidden } from '../ui/Forbidden'
import { toast } from '../ui/notify'
import { Spinner } from '../ui/Spinner'
import { usePageStyles } from '../ui/usePageStyles'
import { CatalogHead, KindPill, OriginPill } from './ServiceCatalogChrome'

/**
 * "What it depends on" - `Service_catalog::dependencies()`, macheta
 * `views/version-3/inventoree/service_catalog/dependencies.php`.
 *
 * Se arata numai muchiile care ajung intr-o ALTA INTRARE DE CATALOG. Restul
 * dependentelor - catre servere, baze de date, aplicatii - sunt tot acolo, dar
 * se citesc pe ecranul de compunere: sunt din ce e FACUT serviciul, nu din ce
 * se mai cere odata cu el.
 *
 * CIFRA "not published" merita capul paginii: o dependenta catre o intrare
 * nepublicata e scrisa corect si totusi tacuta la trimitere, iar asta nu se
 * vede dintr-un rand.
 *
 * Dreptul cerut e cel de CITIRE, ca pe restul catalogului. Un ecran care s-ar
 * deschide numai cu dreptul de scriere ar ascunde de restul echipei de ce
 * anume atarna un serviciu - adica exact informatia pentru care exista
 * catalogul.
 */
export function ServiceCatalogDependenciesPage() {
  const { id } = useParams({ from: '/service_catalog/dependencies/$id' })
  const entryId = Number(id)

  const page = useQuery(serviceCatalogDependenciesQuery(entryId))
  const shell = useQuery(shellQuery(moduleOf(typeof window === 'undefined' ? '' : window.location.pathname)))
  usePageStyles(
    ['css/adm.css', 'css/service_shop.css', 'css/service_catalog.css'],
    shell.data?.version ?? '',
  )

  if (isForbidden(page.error)) return <Forbidden />
  if (!page.data) return <Spinner />

  return (
    <div id="content">
      <DependenciesPanel page={page.data} entryId={entryId} />
    </div>
  )
}

/**
 * Ecranul cu tot cu capul lui, si intreg si in randul desfacut al catalogului.
 *
 * In randul desfacut pleaca DOUA butoane, exact cele doua pe care si macheta
 * veche le pune sub `if (empty($inRow))`: "The catalogue", fiindca lista e
 * chiar dedesubt, si "What it rests on", fiindca e chiar fila de deasupra.
 * Capul de pagina si celelalte doua drumuri raman - la mutare plecase tot, si
 * odata cu el harta, analiza de impact si cifra serviciilor nepublicate.
 */
export function DependenciesPanel({ page, entryId, inRow = false }: {
  page: DependenciesPage
  entryId: number
  inRow?: boolean
}) {
  const client = useQueryClient()
  const [chosen, setChosen] = useState('')
  const [search, setSearch] = useState('')

  const picker = useQuery({
    ...serviceCatalogDependableQuery(entryId, search),
    enabled: page.canWrite,
  })

  const refresh = () => {
    void client.invalidateQueries({ queryKey: ['service-catalog'] })
  }

  const add = useMutation({
    mutationFn: () => addDependency(entryId, Number(chosen)),
    onSuccess: (out) => {
      if (out.status === 'OK') {
        toast(out.message, 'Saved', 'success')
        setChosen('')
        refresh()
        return
      }
      /*
       * Lantul vine ca LISTA de nume, nu lipit intr-un sir de HTML. In portal
       * mesajul intra in pagina ca html si numele treceau prin
       * `htmlspecialchars`; aici il scrie React, care il scapa singur - un
       * serviciu numit "R&D" ar fi iesit altfel stricat.
       */
      toast(out.chain ? `${out.message} ${out.chain.join(' → ')}` : out.message, 'Error', 'error')
    },
  })

  const detach = useMutation({
    mutationFn: (relationId: number) => detachRelation(relationId),
    onSuccess: (out) => {
      toast(out.message, out.status === 'OK' ? 'Detached' : 'Error', out.status === 'OK' ? 'success' : 'error')
      if (out.status === 'OK') refresh()
    },
  })

  const items = picker.data?.items ?? []
  const { entry, dependencies, unpublished } = page

  return (
    <>
      <CatalogHead
        title={entry.name}
        icon="fa-code-branch"
        tone="blue-mauve"
        note="what other services this one depends on — asking for it opens a ticket for each of them as well"
        actions={
          <>
            {!inRow && (
              <>
                <a className="btn btn-default btn-sm" href="/app/service_catalog">
                  <i className="fas fa-arrow-left"></i> The catalogue
                </a>
                <a className="btn btn-default btn-sm" href={`/app/service_catalog/composition/${entry.ID}`}>
                  <i className="fas fa-sitemap"></i> What it rests on
                </a>
              </>
            )}
            <a
              className="btn btn-default btn-sm"
              href={`/app/adm/map?root=${entry.ci_id}`}
              target="_blank"
              rel="noreferrer"
            >
              <i className="fas fa-project-diagram"></i> On the map
            </a>
            <a
              className="btn btn-default btn-sm"
              href={`/app/adm/impact/${entry.ci_id}`}
              target="_blank"
              rel="noreferrer"
            >
              <i className="fas fa-bolt"></i> What breaks with it
            </a>
          </>
        }
        stats={[
          {
            value: dependencies.length,
            label: 'depends on',
            tone: dependencies.length === 0 ? 'grey' : 'blue-mauve',
          },
          { value: unpublished, label: 'not published', tone: unpublished > 0 ? 'yellow' : 'green-mode' },
          { value: entry.kind === 4 ? 'Business' : 'IT', label: 'service', tone: 'grey' },
        ]}
      />

      <div className="cat-cards">
        <div className="adm-card adm-tone-grey">
          <div className="adm-card-head">
            <h5>
              <i className="fas fa-info-circle"></i> One relation, two screens
            </h5>
          </div>
          <p className="adm-policy">
            These are ordinary <strong>depends-on</strong> relations. What you declare here shows on the map and in the
            impact report immediately, and what somebody drew there shows here. It is a second place to edit the same
            thing, not a second copy of it.
          </p>
        </div>

        {page.canWrite && (
          <div className="adm-card adm-tone-blue-mauve">
            <div className="adm-card-head">
              <h5>
                <i className="fas fa-plus"></i> Depends on another service
              </h5>
              <span className="adm-card-note">
                only published catalogue entries — a dependency on a server would never open a ticket, so it is not
                offered here
              </span>
            </div>

            <div className="adm-bar">
              <div className="adm-field adm-field-wide">
                <label htmlFor={`dependsSearch-${entryId}`}>Service</label>
                <input
                  type="text"
                  className="form-control input-sm"
                  placeholder="Search by name"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ marginBottom: 6 }}
                />
                <select
                  className="form-control input-sm"
                  id={`dependsSearch-${entryId}`}
                  value={chosen}
                  onChange={(e) => setChosen(e.target.value)}
                >
                  {/*
                    Golul are propriul cuvant, altul decat "alege unul": o lista
                    goala si una neincarcata inca se repara altfel.
                  */}
                  <option value="">
                    {!picker.data
                      ? 'Loading…'
                      : items.length === 0
                        ? 'Nothing left to choose — every other published service is already here'
                        : 'Choose a service'}
                  </option>
                  {items.map((one) => (
                    <option value={one.catalog_id} key={one.catalog_id}>
                      {one.name} · {one.kind === 4 ? 'Business' : 'IT'}
                    </option>
                  ))}
                </select>
              </div>

              <div className="adm-field">
                <label>&nbsp;</label>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  disabled={add.isPending}
                  onClick={() => {
                    if (chosen === '') {
                      toast('Choose a service first.', 'Nothing chosen', 'error')
                      return
                    }
                    add.mutate()
                  }}
                >
                  <i className="fas fa-link"></i> Depends on it
                </button>
              </div>
            </div>
          </div>
        )}

        {page.unpublished > 0 && (
          <div className="adm-card adm-tone-yellow">
            <div className="adm-card-head">
              <h5>
                <i className="fas fa-exclamation-circle"></i> Silent at request time
              </h5>
            </div>
            <p className="adm-policy">
              <strong>{page.unpublished}</strong> of these {page.unpublished === 1 ? 'is' : 'are'} not published. The
              relation is written and the map has it, but asking for {page.entry.name} will not open a ticket for{' '}
              {page.unpublished === 1 ? 'it' : 'them'} until {page.unpublished === 1 ? 'it is' : 'they are'} published.
            </p>
          </div>
        )}
      </div>

      {page.dependencies.length === 0 ? (
        <div className="adm-empty-inline">
          <i className="fas fa-unlink"></i>
          This service depends on nothing else in the catalogue. Asking for it opens one ticket, and only one.
        </div>
      ) : (
        <table className="table table-hover adm-list">
          <thead>
            <tr>
              <th>Service</th>
              <th>Kind</th>
              <th>Published</th>
              <th>Said by</th>
              <th>Last seen</th>
              <th className="text-right">&nbsp;</th>
            </tr>
          </thead>
          <tbody>
            {page.dependencies.map((one) => (
              <tr key={one.relation_id}>
                <td>
                  <a
                    href={`/app/service_catalog/dependencies/${one.catalog_id}`}
                    title="What that service depends on in turn"
                  >
                    <strong>{one.name}</strong>
                  </a>
                  <br />
                  <span className="adm-muted">{one.class_name}</span>
                </td>
                <td>
                  <KindPill kind={one.kind} />
                </td>
                <td>
                  {one.published === 0 ? (
                    <span className="adm-pill adm-pill-warn" title="No child ticket opens for it while it is unpublished">
                      no
                    </span>
                  ) : (
                    <span className="adm-pill adm-pill-ok">yes</span>
                  )}
                </td>
                <td>
                  <OriginPill origin={one.origin} />
                </td>
                <td className="adm-muted adm-nowrap">{one.last_seen}</td>
                <td className="text-right adm-nowrap">
                  <a
                    className="btn btn-info btn-xs"
                    href={`/app/adm/impact/${one.ci_id}`}
                    title="What else rests on that service"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <i className="fas fa-bolt"></i>
                  </a>
                  {page.canWrite && (
                    <button
                      type="button"
                      className="btn btn-danger btn-xs"
                      title="Stop depending on it — the service itself is untouched"
                      disabled={detach.isPending}
                      onClick={() => {
                        if (
                          !window.confirm(
                            'Stop depending on this service?\n\nThe service itself stays, and so does everything else '
                              + 'about it. Only this dependency goes — on the map too.',
                          )
                        ) {
                          return
                        }
                        detach.mutate(one.relation_id)
                      }}
                    >
                      <i className="fas fa-unlink"></i>
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  )
}

/** Panoul cerut singur, pentru randul desfacut. */
export function DependenciesTab({ entryId }: { entryId: number }) {
  const page = useQuery(serviceCatalogDependenciesQuery(entryId))

  if (isForbidden(page.error)) return <Forbidden />
  if (!page.data) return <Spinner />

  return <DependenciesPanel page={page.data} entryId={entryId} inRow />
}
